import { useInternalState, assignMswData, serializeQuery } from '../hooks/internalState';
import {
  PutMeChannelResult,
  PutMeChannelOptions,
  DeleteMeChannelResult,
  DeleteMeChannelOptions,
  GetMeChannelOptions,
  GetMeChannelResult,
  getMeChannel,
  PutMeChannelFields,
  DeleteMeChannelFields,
  GetMeChannelFields,
  putMeChannel,
  deleteMeChannel,
} from '../fetchers/me/channel';
import { useCallback } from 'react';
import useSWR, { SWRConfiguration, useSWRConfig } from 'swr';
import { useGctlConfig } from '../hooks/GctlProvider';

/**
 * DO NOT MODIFY
 * Auto-generated by gctl-plugin-vimeo-hooks
 */

type UseGetMeChannelOptions<Fields extends GetMeChannelFields> = GetMeChannelOptions<Fields>;

/**
 * Check if a user follows a channel
 * This method determines whether the specified user is a follower of a particular channel.
 */

export function useGetMeChannel<Fields extends GetMeChannelFields>(
  getOptions: UseGetMeChannelOptions<Fields> | (() => UseGetMeChannelOptions<Fields> | null),
  config?: SWRConfiguration<GetMeChannelResult<Fields>>,
) {
  const options = typeof getOptions === 'function' ? getOptions() : getOptions;

  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  return useSWR(
    options ? `/me/channels/${options.where.channelId}${serializeQuery(options)}` : () => null,
    options
      ? () => {
          return getMeChannel({
            ...options,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
            baseUrl,
          });
        }
      : null,
    config,
  );
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetMeChannel, {
    endpoint: '/me/channels/:channelId',
    method: 'GET',
  });
}

/**
 * Check if a user follows a channel
 * This method determines whether the specified user is a follower of a particular channel.
 */

export function useGetMeChannelLazy<Fields extends GetMeChannelFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<GetMeChannelResult<Fields> | undefined>();

  let callback = useCallback(
    async (options: UseGetMeChannelOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/me/channels/${options.where.channelId}${serializeQuery(options)}`,
          getMeChannel({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [callback, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetMeChannelLazy, {
    endpoint: '/me/channels/:channelId',
    method: 'GET',
  });
}

type UsePutMeChannelOptions<Fields extends PutMeChannelFields> = PutMeChannelOptions<Fields>;

/**
 * Subscribe the user to a specific channel
 * This method subscribes the authenticated user to the specified channel.
 */

export function usePutMeChannel<Fields extends PutMeChannelFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<PutMeChannelResult<Fields> | undefined>();

  let call = useCallback(
    async (options: UsePutMeChannelOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/me/channels/${options.where.channelId}${serializeQuery(options)}`,
          putMeChannel({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
          false,
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(usePutMeChannel, {
    endpoint: '/me/channels/:channelId',
    method: 'PUT',
  });
}

type UseDeleteMeChannelOptions<Fields extends DeleteMeChannelFields> =
  DeleteMeChannelOptions<Fields>;

/**
 * Unsubscribe the user from a specific channel
 * This method unsubscribes the authenticated user from the specified channel.
 */

export function useDeleteMeChannel<Fields extends DeleteMeChannelFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<DeleteMeChannelResult<Fields> | undefined>();

  let call = useCallback(
    async (options: UseDeleteMeChannelOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/me/channels/${options.where.channelId}${serializeQuery(options)}`,
          deleteMeChannel({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
          false,
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useDeleteMeChannel, {
    endpoint: '/me/channels/:channelId',
    method: 'DELETE',
  });
}
